/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  theme,
  Box,
  convertCssUnit,
  convertResponsiveValue,
  Theme,
  styled,
  Text,
  Platform,
} from '@staffino/primitive-controls';
import NumberButton from '../components/number-button';
import lerpScoreColors from './lerp-score-colors';

const colorPalette = [
  '#fc5b02',
  '#fd7402',
  '#fd8d01',
  '#fea501',
  '#febe00',
  '#ffd800',
  '#ccd109',
  '#99cb12',
  '#66c51c',
  '#33bf25',
  '#00b92e',
];

type LabelTextProps = {
  theme?: Theme;
  left?: string;
};

const LabelText = styled(Text)<LabelTextProps>((props) => ({
  fontWeight: 'bold',
  fontSize: convertCssUnit('0.875rem'),
  wordBreak: 'normal' as const,
  left: props.left,
  color: '#a3b2bf',
  [props.theme.mq.small]: {
    marginLeft: 0,
    transform: 'translate(-50%)',
  },
  [props.theme.mq.large]: {
    whiteSpace: 'nowrap',
  },
}));

type Props = {
  name: string;
  skin: string;
  lowerBoundLabel?: string;
  upperBoundLabel?: string;
  lowerBoundScore?: number;
  upperBoundScore?: number;
  colorStyle?: string;
  value?: number;
  onChange?: (score: number) => void;
} & React.ComponentProps<typeof Box>;

const NumbersControl: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<unknown>
> = React.forwardRef(
  (
    {
      name,
      skin,
      lowerBoundLabel,
      upperBoundLabel,
      lowerBoundScore = 0,
      upperBoundScore = 10,
      colorStyle,
      value,
      onChange,
      ...rest
    }: Props,
    ref: React.Ref<unknown>,
  ) => {
    const { t } = useTranslation();
    const numbers = useMemo(
      () => lerpScoreColors(lowerBoundScore, upperBoundScore, colorPalette),
      [lowerBoundScore, upperBoundScore],
    );
    const scoreIsEmpty = Number.isNaN(parseInt(value?.toString(), 10));

    const numberCount = upperBoundScore - lowerBoundScore + 1;
    // no need to calculate spacerSize for react-native, number boxes are all wide as the screen width
    const spacerSize = (10 - numberCount + lowerBoundScore) * 0.75;
    const labelsPosition = Platform.select<string>({
      native: '0%',
      default: '50%',
    });

    const handleChange = useCallback(
      (score) => onChange?.(parseInt(score, 10)),
      [onChange],
    );

    if (numberCount <= 0) {
      return null;
    }

    return (
      <Box
        {...rest}
        // flex={1}
        flexDirection={'row' as const}
        flexWrap="wrap"
        justifyContent="center"
      >
        <Box
          width={1}
          flexGrow={1}
          flexShrink={0}
          flexBasis="auto"
          flexDirection={'row' as const}
          flexWrap="wrap"
          justifyContent="center"
          px={convertCssUnit([
            '1.5rem',
            '1.75rem',
            theme.mq.tablet ? '0rem' : '1rem',
            numbers?.length > 10 ? 0 : '0.75rem',
          ])}
          order={convertResponsiveValue([1, 3])}
        >
          <Box
            flexGrow={1}
            flexShrink={0}
            flexBasis="auto"
            my={2}
            display={convertResponsiveValue(['none', 'flex'])}
            pl={convertCssUnit([0, `${spacerSize}rem`])}
          >
            <LabelText
              mr={1}
              textAlign="left"
              fontWeight="bold"
              color="lightText"
              left={labelsPosition}
            >
              {lowerBoundLabel || t('Not likely')}
            </LabelText>
          </Box>
          <Box
            flexGrow={1}
            flexShrink={0}
            flexBasis="auto"
            display={convertResponsiveValue(['none', 'flex'])}
          />
          <Box
            flexGrow={1}
            flexShrink={0}
            my={2}
            pr={convertCssUnit([0, `${spacerSize}rem`])}
          >
            <LabelText
              fontSize={convertCssUnit('0.75rem')}
              textAlign={convertResponsiveValue(['center', 'right'])}
              fontWeight="bold"
              color="lightText"
              left={labelsPosition}
            >
              {upperBoundLabel || t('Extremely likely')}
            </LabelText>
          </Box>
        </Box>

        <Box
          width={1}
          flexGrow={1}
          flexShrink={0}
          flexBasis="auto"
          flexDirection={'row' as const}
          flexWrap={convertResponsiveValue(['wrap-reverse', 'wrap'])}
          justifyContent="center"
          order={2}
        >
          {numbers.map((number, index) =>
            ['Numbers', 'numbers'].includes(skin) ? (
              <NumberButton
                key={index}
                color={colorStyle}
                inactiveColor={
                  scoreIsEmpty ? '#ffffff' : theme.colors.separator
                }
                score={number.score}
                name={name}
                innerRef={ref}
                colored={false}
                first={index === 0}
                last={index === numbers.length - 1}
                isSelected={number.score === value}
                onChange={handleChange}
              />
            ) : (
              <NumberButton
                key={number.color}
                color={number.color}
                inactiveColor={
                  scoreIsEmpty ? number.color : theme.colors.separator
                }
                score={number.score}
                name={name}
                innerRef={ref}
                colored
                first={index === 0}
                last={index === numbers.length - 1}
                isSelected={number.score === value}
                onChange={handleChange}
              />
            ),
          )}
        </Box>

        <Box
          my={convertCssUnit(2)}
          mx={convertCssUnit('3px')}
          display={convertResponsiveValue(['flex', 'none'])}
          order={convertResponsiveValue([3, 1])}
        >
          <LabelText
            mr={convertCssUnit(1)}
            textAlign="center"
            fontWeight="bold"
            color="lightText"
            left={labelsPosition}
          >
            {lowerBoundLabel || t('Not likely')}
          </LabelText>
        </Box>
      </Box>
    );
  },
);

export default NumbersControl;
